import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import PublicHeader from '../components/Layout/PublicHeader';
import StaticPageTopbar from '../components/Layout/StaticPageTopbar';
import radsportMark from '../assets/brand/radsport-mark.svg';
import PartnerPlacements from '../components/PartnerPlacements';
import { submitPartnerInquiry } from '../services/inquiryService';
import { umamiTrack } from '../services/analytics';
import { SITE_ORIGIN } from '../config/siteOrigin';

// ── Marketing-Seite „Für Partner" ─────────────────────────────────────────────────────────
// Hero, die drei Platzierungen (`PartnerPlacements`) und der Kontakt-Dialog. Die alte
// Vorstellung der vier Werbeplätze war am 04.09.2026 entfallen, weil der Veranstalter-Bereich
// dieselben Leistungen dort verkauft, wo sie gebucht werden — übrig blieb eine Seite, auf der
// niemand erkennen konnte, worüber er anfragt. Seit dem 21.09.2026 deutet sie die Möglichkeiten
// wieder an, diesmal ohne Preise: die werden je Marke verhandelt.
// Bewusst ohne Reichweiten- und Nutzungszahlen: die gehören ins Gespräch und ins Mediakit,
// wo sie gepflegt werden. Eine Zahl auf einer Marketingseite altert unbemerkt.

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

const PAGE_TITLE = 'Werben auf Radsport-Events.de – Für Partner | Radsport Events';
const PAGE_DESC =
  'Werben auf Radsport-Events.de: Erreichen Sie eine engagierte Radsport-Community im Moment der Event-Planung. Sprechen Sie uns an, wir gestalten die Zusammenarbeit gemeinsam mit Ihnen.';
const CANONICAL = `${SITE_ORIGIN}/fuer-partner`;

type FieldErrors = { name?: string; email?: string };

/** Beanstandung unter einem Feld — role="alert", damit Screenreader sie ansagen. */
const FieldError: React.FC<{ id: string; text?: string }> = ({ id, text }) =>
  text ? (
    <div id={id} role="alert" style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: '#C0392B', marginTop: 5 }}>
      {text}
    </div>
  ) : null;

// Berry-Sternchen zur Kennzeichnung von Pflichtfeldern (Name, E-Mail) im Kontaktformular.
const RequiredMark: React.FC = () => (
  <span aria-hidden style={{ color: '#922C57', marginLeft: 3 }}>*</span>
);

const FuerPartner: React.FC = () => {
  // `?via=banner` hängt die Haus-Anzeige „Dieser Platz ist zu haben" auf der Startseite an. Eine
  // so entstandene Anfrage trägt im Posteingang das Thema „Werbeplatz" — damit ist zählbar, was
  // der Banner einbringt, ohne eine zweite Auswertung dafür zu bauen.
  const [params] = useSearchParams();
  const viaBanner = params.get('via') === 'banner';

  const [contactOpen, setContactOpen] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [mail, setMail] = useState('');
  const [msg, setMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openContact = useCallback(() => {
    setContactSent(false);
    setError(null);
    setContactOpen(true);
  }, []);
  const closeContact = useCallback(() => setContactOpen(false), []);

  // Modal: Escape schließt, Body-Scroll sperren solange offen.
  useEffect(() => {
    if (!contactOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContact();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [contactOpen, closeContact]);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(mail.trim());

  const [errors, setErrors] = useState<FieldErrors>({});
  const nameRef = useRef<HTMLInputElement>(null);
  const mailRef = useRef<HTMLInputElement>(null);

  // Spam-Abwehr wie auf der Kontaktseite: verstecktes Feld plus Dauer bis zum Absenden.
  // Ausgewertet in InquirySpamCheck; abgewiesen wird nichts, nur einsortiert.
  const [website, setWebsite] = useState('');
  const mountedAt = useRef(Date.now());

  // Gleiches Muster wie auf der Kontaktseite: der Button bleibt klickbar, die Beanstandung steht
  // am betroffenen Feld. Ein ausgegrauter Button verrät nicht, *welches* Feld gemeint ist.
  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = 'Bitte tragen Sie Ihren Namen ein.';
    if (!mail.trim()) next.email = 'Bitte tragen Sie Ihre E-Mail-Adresse ein.';
    else if (!emailValid) next.email = 'Diese Adresse sieht nicht vollständig aus — es fehlt etwas wie „@anbieter.de".';
    return next;
  };

  /** Was noch fehlt, in einem Satz — steht unter dem Button, bevor jemand ihn drückt. */
  const missingHint: string | null = (() => {
    if (submitting) return null;
    const missing: string[] = [];
    if (!name.trim()) missing.push('Ihr Name');
    if (!mail.trim()) missing.push('Ihre E-Mail-Adresse');
    if (missing.length === 0) return emailValid ? null : 'Die E-Mail-Adresse ist noch unvollständig.';
    return `Es fehlt noch ${missing.length > 1 ? `${missing[0]} und ${missing[1]}` : missing[0]}.`;
  })();

  const submit = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError(null);
      (found.name ? nameRef : mailRef).current?.focus();
      return;
    }
    if (!name.trim()) {
      setError('Bitte geben Sie Ihren Namen an.');
      return;
    }
    if (!emailValid) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse an.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitPartnerInquiry({
        name: name.trim(),
        company: company.trim() || undefined,
        email: mail.trim(),
        topic: viaBanner ? 'Werbeplatz' : undefined,
        message: msg.trim() || undefined,
        website,
        elapsedMs: Date.now() - mountedAt.current,
      });
      umamiTrack('partner-anfrage', { eingang: viaBanner ? 'banner' : 'direkt' });
      setContactSent(true);
    } catch {
      setError('Das hat leider nicht geklappt. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: 6,
    border: '1px solid #E5E1E1',
    borderRadius: 10,
    padding: '11px 12px',
    fontFamily: FONT_BODY,
    fontSize: 15,
    color: '#2C2C2C',
  };
  /** Eingabefeld, rot umrandet sobald es beanstandet ist. */
  const fieldStyle = (invalid?: boolean): React.CSSProperties => ({
    ...inputStyle,
    border: `1px solid ${invalid ? '#C0392B' : '#E5E1E1'}`,
  });
  const labelStyle: React.CSSProperties = {
    fontFamily: FONT_MONO,
    fontSize: 10,
    letterSpacing: '0.1em',
    color: '#9A9494',
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
      </Helmet>

      <style>{`
        @keyframes fpRise { from { opacity: 0; transform: translateY(26px); } to { opacity: 1; transform: translateY(0); } }
        .fp-rise { animation: fpRise 0.6s ease both; }

        @media (prefers-reduced-motion: reduce) {
          .fp-rise { animation: none !important; opacity: 1 !important; transform: none !important; }
        }

        /* Responsive: Hero zu Column, Formularzeile untereinander. */
        @media (max-width: 860px) {
          .fp-container { padding: 0 20px 56px !important; }
          .fp-hero { flex-direction: column !important; align-items: flex-start !important; padding: 40px 28px !important; }
          .fp-hero-square { display: none !important; }
          .fp-hero-mark { width: 220px !important; }
          .fp-hero-h1 { font-size: 38px !important; }
          .fp-modal-row { flex-direction: column !important; gap: 14px !important; }
        }
      `}</style>

      <div className="hide-mobile">
        <PublicHeader />
      </div>
      <StaticPageTopbar label="// FÜR PARTNER" />

      <div className="fp-container" style={{ flex: 1, maxWidth: 1180, width: '100%', margin: '0 auto', padding: '0 40px 72px' }}>

        {/* HERO */}
        <section
          className="fp-hero fp-rise"
          style={{
            position: 'relative', overflow: 'hidden', marginTop: 24, borderRadius: 24,
            background: 'linear-gradient(115deg,#3A1523 0%,#6E2440 54%,#A8355F 100%)',
            color: '#F5F3F3', padding: '52px 56px', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 32,
          }}
        >
          <div className="fp-hero-square" style={{ position: 'absolute', right: 230, top: -60, width: 260, height: 260, background: '#C25078', opacity: 0.16, transform: 'rotate(45deg)', borderRadius: 52 }} />
          <div style={{ position: 'relative', maxWidth: 600 }}>
            <span style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.18em', color: 'rgba(245,243,243,0.7)' }}>{'// WERBEN AUF RADSPORT-EVENTS.DE'}</span>
            <h1 className="fp-hero-h1" style={{ fontFamily: FONT_DISPLAY, fontWeight: 900, fontSize: 52, lineHeight: 1.02, letterSpacing: '-0.02em', margin: '16px 0 0' }}>
              Sichtbar, wo die Rad&shy;saison geplant wird.
            </h1>
            <p style={{ fontFamily: FONT_BODY, fontSize: 18, lineHeight: 1.5, color: 'rgba(245,243,243,0.86)', margin: '18px 0 0', maxWidth: 520 }}>
              Erreichen Sie Radsportler dort, wo sie ihre nächsten Events entdecken und ihre Saison planen. Wir entwickeln gemeinsam die passende Platzierung für Ihre Marke.
            </p>
            <button
              type="button"
              onClick={openContact}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 26, background: '#F5F3F3', color: '#3A1523', border: 'none', padding: '15px 28px', borderRadius: 13, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
            >
              Partnerschaft anfragen<span style={{ fontFamily: FONT_MONO }}>→</span>
            </button>
          </div>
          <div style={{ position: 'relative', flexShrink: 0, width: 300, height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={radsportMark} alt="" aria-hidden className="fp-hero-mark" style={{ width: 300, height: 'auto', filter: 'brightness(0) invert(1)', opacity: 0.92 }} />
          </div>
        </section>

        <PartnerPlacements />

        {/* Logo & Verlinkung hatte keinen eigenen Platz mehr im Menü (57 Sitzungen in 90 Tagen, nie über
            Google); wer es braucht, ist Partner und landet hier. */}
        <p style={{ fontFamily: FONT_BODY, fontSize: 15, color: '#6A6A6A', margin: '32px 0 0' }}>
          Logo, Banner und Textlinks für eure Website: <Link to="/logo" style={{ color: '#922C57', fontWeight: 700 }}>Logo & Verlinkung</Link>
        </p>
      </div>

      {/* KONTAKT-MODAL */}
      {contactOpen && (
        <div
          onClick={closeContact}
          role="dialog"
          aria-modal="true"
          aria-label="Partner werden – Kontakt"
          style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(44,20,30,0.5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, overflowY: 'auto' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', margin: 'auto', width: '100%', maxWidth: 520, background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 30px 70px rgba(38,20,28,0.4)' }}
          >
            {!contactSent ? (
              <>
                <div style={{ background: 'linear-gradient(115deg,#3A1523,#6E2440)', color: '#F5F3F3', padding: '26px 30px' }}>
                  <button
                    type="button"
                    onClick={closeContact}
                    aria-label="Schließen"
                    style={{ position: 'absolute', top: 16, right: 18, cursor: 'pointer', fontSize: 20, color: 'rgba(245,243,243,0.7)', lineHeight: 1, background: 'none', border: 'none' }}
                  >✕</button>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: '0.16em', color: 'rgba(245,243,243,0.7)' }}>{'// PARTNER WERDEN'}</span>
                  <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 26, letterSpacing: '-0.01em', margin: '8px 0 0' }}>Lassen Sie uns sprechen</h3>
                </div>
                <div style={{ padding: '24px 30px 28px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <p style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#9A9494', margin: 0 }}>
                    Mit <span style={{ color: '#922C57' }}>*</span> markierte Felder sind Pflichtfelder.
                  </p>
                  <div className="fp-modal-row" style={{ display: 'flex', gap: 14 }}>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle} htmlFor="fp-name">NAME<RequiredMark /></label>
                      <input
                        id="fp-name" ref={nameRef} value={name}
                        onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: undefined })); }}
                        onBlur={() => setErrors((p) => ({ ...p, name: validate().name }))}
                        style={fieldStyle(!!errors.name)} required aria-required="true"
                        aria-invalid={!!errors.name} aria-describedby={errors.name ? 'fp-name-err' : undefined}
                      />
                      <FieldError id="fp-name-err" text={errors.name} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={labelStyle} htmlFor="fp-company">UNTERNEHMEN</label>
                      <input id="fp-company" value={company} onChange={(e) => setCompany(e.target.value)} style={inputStyle} />
                    </div>
                  </div>
                  <div>
                    <label style={labelStyle} htmlFor="fp-mail">E-MAIL<RequiredMark /></label>
                    <input
                      id="fp-mail" ref={mailRef} type="email" value={mail}
                      onChange={(e) => { setMail(e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: undefined })); }}
                      onBlur={() => setErrors((p) => ({ ...p, email: validate().email }))}
                      style={fieldStyle(!!errors.email)} required aria-required="true"
                      aria-invalid={!!errors.email} aria-describedby={errors.email ? 'fp-mail-err' : undefined}
                    />
                    <FieldError id="fp-mail-err" text={errors.email} />
                  </div>
                  <div>
                    <label style={labelStyle} htmlFor="fp-msg">NACHRICHT</label>
                    <textarea
                      id="fp-msg"
                      value={msg}
                      onChange={(e) => setMsg(e.target.value)}
                      placeholder="Erzählen Sie uns kurz von Ihrem Vorhaben …"
                      style={{ ...inputStyle, resize: 'none', height: 80 }}
                    />
                  </div>
                  {/* Honeypot — aus dem Bild geschoben, fuer Menschen unsichtbar. */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: -9999, top: 0, width: 1, height: 1, overflow: 'hidden' }}>
                    <label htmlFor="fp-website">Website (bitte frei lassen)</label>
                    <input
                      id="fp-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                      value={website} onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    style={{ width: '100%', background: '#922C57', color: '#fff', border: 'none', padding: 14, borderRadius: 12, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 16, cursor: submitting ? 'progress' : 'pointer', opacity: submitting ? 0.6 : 1 }}
                  >{submitting ? 'Wird gesendet …' : 'Anfrage senden'}</button>
                  {error ? (
                    <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: '#B03A3A', textAlign: 'center', margin: 0 }}>{error}</p>
                  ) : missingHint ? (
                    <p style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#9A9494', textAlign: 'center', margin: 0 }}>{missingHint}</p>
                  ) : null}
                </div>
              </>
            ) : (
              <div style={{ padding: '44px 34px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={closeContact}
                  aria-label="Schließen"
                  style={{ position: 'absolute', top: 16, right: 18, cursor: 'pointer', fontSize: 20, color: '#C4BFBF', lineHeight: 1, background: 'none', border: 'none' }}
                >✕</button>
                <div style={{ width: 60, height: 60, borderRadius: 16, background: '#922C57', transform: 'rotate(45deg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ transform: 'rotate(-45deg)', color: '#fff', fontSize: 30 }}>✓</span>
                </div>
                <h3 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 24, color: '#2C2C2C', margin: '22px 0 0' }}>Vielen Dank!</h3>
                <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.5, color: '#6A6A6A', margin: '10px 0 0', maxWidth: 340 }}>
                  Wir haben Ihre Anfrage erhalten und melden uns in Kürze persönlich bei Ihnen.
                </p>
                <button
                  type="button"
                  onClick={closeContact}
                  style={{ marginTop: 22, background: '#fff', border: '1px solid #E5E1E1', color: '#922C57', padding: '12px 24px', borderRadius: 11, fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                >Schließen</button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default FuerPartner;
